<template>
  <main>
    <h1>🛠️ Willkommen bei Codewalker.net CMS</h1>
    <div>
      <img id="centerpic250" :src="logo" alt="Logo" />
    </div>

    <div class="language-switcher">
      <button @click="switchLang('de')">
        <img :src="deFlag" alt="Deutsch" width="24" height="18"/>
      </button>
      <button @click="switchLang('en')">
        <img :src="enFlag" alt="US" width="24" height="18"/>
      </button>
    </div>

    <section v-if="language === 'de'">
      <h2>🚧 Work in progress – aber richtig.</h2>
      <p>
        Wir bauen ein CMS, das nicht klotzt, sondern kompiliert.<br>
        Kein aufgeblasenes WordPress, kein PHP-Chaos. Sondern:<br>
        <strong>Markdown first. Vue.js im Frontend. Node.js + TypeScript im Backend.</strong><br><br>

        🗃️ <strong>Content liegt als Markdown vor</strong> – roh, versionierbar, menschenlesbar.<br>
        🧠 <strong>Gerendert wird nur im Client</strong> – der Browser übernimmt die harte Arbeit.<br>
        📦 Server liefert nur Markdown + Scripts aus. That’s it.<br>
        Keine Template-Engine, kein HTML-Gematsche.<br><br>

        🔧 Modulares System, komplett in <strong>TypeScript</strong> gebaut.<br>
        🛢️ <strong>SQLite</strong> für Entwicklung und Offline-Desktop-Version.<br>
        🧱 <strong>MariaDB</strong> im Onlinebetrieb – wenn’s sein muss.<br><br>

        📉 <strong>SEO-HTML?</strong> Optional. Wenn’s sein muss. Für Bots.<br>
        Für Menschen gibt’s echte Inhalte – direkt im Browser.<br><br>

        🔒 Kein Cloud-Zwang. Kein Vendor-Lock.
      </p>
    </section>

    <section v-else>
      <h2>🚧 Work in progress – for real.</h2>
      <p>
        We're building a CMS that compiles instead of bloats.<br>
        No bloated WordPress. No PHP spaghetti. Just:<br>
        <strong>Markdown first. Vue.js in the frontend. Node.js + TypeScript in the backend.</strong><br><br>

        🗃️ <strong>Content is stored as raw Markdown</strong> – clean, versionable, human-readable.<br>
        🧠 <strong>Rendering happens in the browser</strong> – the client does the heavy lifting.<br>
        📦 The server only delivers Markdown and JavaScript.<br>
        That’s it. No template engines. No HTML soup.<br><br>

        🔧 Modular system, fully built in <strong>TypeScript</strong>.<br>
        🛢️ <strong>SQLite</strong> for development and offline desktop use.<br>
        🧱 <strong>MariaDB</strong> in production – if needed.<br><br>

        📉 <strong>SEO-HTML?</strong> Optional. For bots only.<br>
        Real users get real content – rendered live in the browser.<br><br>

        🔒 No cloud lock-in. No vendor trap.
      </p>
    </section>

    <section>
      <h2>📝 Geplante Kernfunktionen:</h2>
      <ul>
        <li>Markdown als primäre Inhaltssprache</li>
        <li>Clientseitiges Rendering für maximale Flexibilität</li>
        <li>Modularer Aufbau mit Vue.js im Frontend</li>
        <li>Serverlogik in TypeScript mit SQLite und später MariaDB</li>
        <li>Statische HTML-Erzeugung für SEO</li>
        <li>Erstellung alle Beiträge in Markdown und gleichzeitig schreibt man ein Buch</li>
      </ul>
    </section>

    <section>
      <p>{{ serverInfo }}</p>
    </section>

    <footer>
      <p>&copy; 2025 Codewalker.net – Adam Skotarczak -
        <a href="https://www.ionivation.com/impressum">Impressum</a>
      </p>
    </footer>
  </main>
</template>

<script setup lang="ts">
  import { ref, onMounted } from 'vue'
  import { fetchServerInfo } from '../modules/serverinfo'
  import { getLanguage, setLanguage as persistLang } from '../modules/languageSwitcher'
  import deFlag from '../assets/img/flags/de.svg'
  import enFlag from '../assets/img/flags/gb.svg'
  import logo from '../assets/img/logo_256x256.png'
  import css from '../assets/css/styles.css'

  const language = ref<'de' | 'en'>(getLanguage())
  const serverInfo = ref('⏳ Lade Serverinfo…')

  function switchLang(lang: 'de' | 'en') {
    language.value = lang
    persistLang(lang)
  }

  onMounted(async () => {
    serverInfo.value = await fetchServerInfo()
  })
</script>

<style scoped>
  .language-switcher {
    display: flex;
    gap: 0.5rem;
    margin: 1rem 0;
  }
  img#centerpic250 {
    max-width: 250px;
    height: auto;
    margin: 1rem auto;
    display: block;
  }
</style>
